import { useCallback, useEffect, useMemo, useState } from 'react'
import { configApi } from '../services/api'
import { inferCategoryModulo, normalizeModuloValue } from '../config/modules'

const fallback = {
  version: 1,
  categories: [
    {
      name: 'Acción Social y Salud',
      options: [
        { value: 'medicamentos', label: 'Medicamentos' },
        { value: 'alimentos', label: 'Alimentos' },
        { value: 'ropas', label: 'Ropas' },
        { value: 'indumentaria', label: 'Indumentaria' },
        { value: 'calzado', label: 'Calzado' },
        { value: 'sepelio', label: 'Servicios Fúnebres / Sepelio' },
        { value: 'servicios_funebres', label: 'Servicios Fúnebres' },
        { value: 'otros', label: 'Otros' },
      ],
    },
  ],
}

const normalize = (data) => {
  if (!data || !Array.isArray(data.categories)) return fallback
  return {
    version: 1,
    categories: data.categories
      .filter((c) => c && typeof c === 'object')
      .map((c) => ({
        name: String(c.name || '').trim(),
        module: inferCategoryModulo(c),
        options: Array.isArray(c.options)
          ? c.options
              .filter((o) => o && typeof o === 'object')
              .map((o) => ({
                value: String(o.value || '').trim(),
                label: String(o.label || '').trim(),
              }))
              .filter((o) => o.value && o.label)
          : [],
      }))
      .filter((c) => c.name && c.options.length > 0),
  }
}

export const useAssistanceTypes = (options = {}) => {
  return useAssistanceTypesWithOptions(options)
}

export const useAssistanceTypesWithOptions = (options = {}) => {
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState(fallback)
  const modulo = normalizeModuloValue(options.modulo)

  const load = useCallback(async () => {
    try {
      setLoading(true)
      const resp = await configApi.getAssistanceTypes()
      setData(normalize(resp.data.data))
    } catch (e) {
      setData(fallback)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const flatOptions = useMemo(() => {
    return data.categories.flatMap((c) => c.options)
  }, [data.categories])

  const filteredData = useMemo(() => {
    if (!modulo || modulo === 'all' || modulo === 'administracion') return data
    return {
      ...data,
      categories: data.categories.filter((category) => {
        const categoryModulo = normalizeModuloValue(category.module)
        return categoryModulo === 'all' || categoryModulo === modulo
      })
    }
  }, [data, modulo])

  const visibleOptions = useMemo(() => {
    return filteredData.categories.flatMap((category) => category.options)
  }, [filteredData.categories])

  const labelByValue = useMemo(() => {
    const map = new Map()
    for (const opt of flatOptions) map.set(opt.value, opt.label)
    return map
  }, [flatOptions])

  const firstValue = visibleOptions[0]?.value || flatOptions[0]?.value || 'medicamentos'

  return {
    loading,
    data: filteredData,
    flatOptions: visibleOptions,
    labelByValue,
    firstValue,
    reload: load,
  }
}
